import { useOptimisticUpdateMutation } from "../../../hooks/useOptimisticUpdateMutation";
import {
  ExerciseAssetCoachInfoDto,
  ExerciseAssetDto,
  ExerciseAssetType,
  ExerciseTagDto,
  ICreateExerciseAssetCommand,
  PaginatedListOfExerciseAssetDto,
} from "@growth-machine-llc/stridist-api-client";

import ExerciseAssetsService from "../../../services/ExerciseAssetsService";
import { COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY } from "../ExercisesTableScreen";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE } from "./useArchiveRestoreLibraryExercise";
import { useQueryClient } from "@tanstack/react-query";

import { useExerciseListFilterContext } from "../../../contexts/ExerciseLibraryFilterContext";

export const CREATE_EXERCISE_MESSAGE = "Library exercise created.";

export function useCreateLibraryExercise(
  exerciseTags?: ExerciseTagDto[],
  successMessage: string = CREATE_EXERCISE_MESSAGE,
) {
  const {
    id: coachId,
    email: coachEmail,
    photoUrl: coachPhotoUrl,
    displayName: coachName,
  } = useCurrentUser();
  const queryClient = useQueryClient();

  const { exerciseType } = useExerciseListFilterContext() || {
    exerciseType: null,
  };

  return useOptimisticUpdateMutation({
    queryKey: [COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY],
    mutationFn: ExerciseAssetsService.createExerciseAsset,
    successToastMessage: successMessage,
    optimisticUpdater: {
      updateFn: (
        oldData: PaginatedListOfExerciseAssetDto,
        variables: ICreateExerciseAssetCommand,
        tempId: number,
      ) => {
        if (
          exerciseType === ExerciseAssetType.PUBLIC ||
          exerciseType === ExerciseAssetType.TEAM
        ) {
          return oldData;
        }

        const newItem = {
          ...variables,
          id: tempId,
          coachId,
          coach: {
            coachId,
            coachName,
            coachEmail,
            coachPhotoUrl,
          } as ExerciseAssetCoachInfoDto,
          archived: false,
          isPendingApproval: false,
          tags: exerciseTags,
        };

        if (exerciseType === ExerciseAssetType.ALL) {
          const publicOrTeamCopyIndex = oldData.items.findIndex(
            (item) =>
              item.name.trim() === variables.name.trim() &&
              item?.coachId !== coachId,
          );

          if (publicOrTeamCopyIndex !== -1) {
            oldData.items.splice(
              publicOrTeamCopyIndex,
              1,
              ExerciseAssetDto.fromJS(newItem),
            );
            return oldData;
          }
        }

        oldData.items.unshift(ExerciseAssetDto.fromJS(newItem));
        oldData.totalCount += 1;
        return oldData;
      },
    },
    invalidateQueryOptions: {
      enabled: true,
      refetchType: "none",
    },
    exactQueryKey: false,
    successUpdater: {
      updateFn: (oldData, variables, tempId) => {
        oldData.items = oldData.items?.map((asset) => {
          if (asset.id === tempId) {
            asset.id = variables;
          }
          return asset;
        });
        return oldData;
      },
    },
    options: {
      onSuccess: () => {
        INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE.forEach((queryKey) => {
          queryClient.invalidateQueries({
            queryKey: [queryKey],
            exact: false,
          });
        });
      },
    },
  });
}
